/**
 * UUID v7 `Idempotency-Key` 生成 —— 框架通道配置等写端点要求 UUID v7（A8 §10「写入端点要求 UUID v7
 * `Idempotency-Key`」，服务端按 `^…-7…-[89ab]…$` 校验，v4 会被 `VALIDATION_FAILED` 拒绝）。
 *
 * 布局（RFC 9562 §5.7）：48 位 Unix 毫秒时间戳 + 版本 7 + 12 位随机 + variant `10` + 62 位随机。
 * 随机源优先 `crypto.getRandomValues`；缺失时退化为 Math.random —— 幂等键不是秘密，只需在同一时刻
 * 不与他人的键重复，时间戳前缀已把碰撞面压到同一毫秒内。
 */

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
    return bytes;
  }
  for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return bytes;
}

/** 生成一枚 UUID v7（小写、带连字符）。`nowMs` 仅供测试固定时间戳。 */
export function newIdempotencyKeyV7(nowMs: number = Date.now()): string {
  const bytes = randomBytes(16);
  // 48 位大端毫秒时间戳：用除法拆高 16 位，避免位运算把数截成 32 位
  const ms = Math.max(0, Math.floor(nowMs));
  const high = Math.floor(ms / 2 ** 32);
  const low = ms >>> 0;
  bytes[0] = (high >>> 8) & 0xff;
  bytes[1] = high & 0xff;
  bytes[2] = (low >>> 24) & 0xff;
  bytes[3] = (low >>> 16) & 0xff;
  bytes[4] = (low >>> 8) & 0xff;
  bytes[5] = low & 0xff;
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70; // version 7
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
